import { useEffect, useState } from "react";
import {
  Brain,
  Calendar,
  Clock,
  Pencil,
  Trash2,
  UploadCloud,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  SavedPrediction,
  listPredictions,
  deletePrediction,
  renamePrediction,
  SAVED_PREDICTION_LIMITS,
} from "@/services/savedPredictionsService";
import type { SubscriptionTier } from "@/utils/subscriptionUtils";

interface PredictionHistoryProps {
  onLoad: (saved: SavedPrediction) => void;
  refreshKey?: number;
  tier?: SubscriptionTier;
}

export default function PredictionHistory({
  onLoad,
  refreshKey,
  tier = "PAYG",
}: PredictionHistoryProps) {
  const { toast } = useToast();
  const [items, setItems] = useState<SavedPrediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  async function load() {
    setLoading(true);
    try {
      const rows = await listPredictions();
      setItems(rows);
    } catch {
      toast({
        title: "Error",
        description: "Could not load prediction history.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  async function handleDelete(id: string) {
    try {
      await deletePrediction(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast({
        title: "Deleted",
        description: "Prediction removed from history.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Could not delete prediction.",
        variant: "destructive",
      });
    }
  }

  function startRename(item: SavedPrediction) {
    setRenamingId(item.id);
    setRenameValue(item.label);
  }

  async function commitRename(id: string) {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    try {
      await renamePrediction(id, trimmed);
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, label: trimmed } : i)),
      );
      toast({ title: "Renamed", description: "Label updated." });
    } catch {
      toast({
        title: "Error",
        description: "Could not rename prediction.",
        variant: "destructive",
      });
    } finally {
      setRenamingId(null);
    }
  }

  function getDeforestationStat(item: SavedPrediction): string {
    const p = item.predictions as Record<string, number>;
    const key =
      item.timeframe === "10y"
        ? "deforestation_rate_10y"
        : "deforestation_rate_5y";
    const val = p[key];
    return val !== undefined ? `${val.toFixed(1)}%` : "—";
  }

  function getTimeframeColor(timeframe: string) {
    return timeframe === "10y"
      ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30"
      : "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-24 h-24 bg-[#40e0d0]/10 rounded-full flex items-center justify-center mb-6">
          <Brain className="w-12 h-12 text-[#40e0d0]" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          No Saved Predictions
        </h3>
        <p className="text-muted-foreground max-w-md text-sm">
          After running a prediction on the Projections tab, click{" "}
          <strong>Save Prediction</strong> to store it here for later reference.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Count / limit header */}
      {(() => {
        const limit = SAVED_PREDICTION_LIMITS[tier];
        const pct = Math.min((items.length / limit) * 100, 100);
        const atLimit = items.length >= limit;
        return (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-sm text-muted-foreground">
                <span className={atLimit ? "text-red-500 font-semibold" : ""}>
                  {items.length}
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  / {limit} saved projection{limit !== 1 ? "s" : ""}
                </span>
              </p>
              {atLimit && (
                <span className="text-xs text-red-500 font-medium">
                  Limit reached
                </span>
              )}
            </div>
            <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  pct > 70
                    ? "bg-red-500"
                    : pct >= 60
                      ? "bg-amber-400"
                      : "bg-[#40e0d0]"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-4">
        {items.map((item) => {
          const defoStat = getDeforestationStat(item);
          const isRenaming = renamingId === item.id;

          return (
            <Card
              key={item.id}
              className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 text-left">
                    {isRenaming ? (
                      <div className="flex items-start gap-1">
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename(item.id);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          className="h-7 text-sm"
                          autoFocus
                        />
                        <button
                          onClick={() => commitRename(item.id)}
                          className="text-green-500 hover:text-green-600"
                          title="Confirm rename"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setRenamingId(null)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Cancel rename"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm truncate" title={item.label}>
                        <span className="font-semibold">{item.label}</span>
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 font-semibold uppercase tracking-wide">
                      {item.region_continent || item.region_name}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`shrink-0 text-xs ${getTimeframeColor(item.timeframe)}`}
                  >
                    {item.timeframe === "10y" ? "10 Years" : "5 Years"}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Quick stat */}
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                  <span className="text-xs text-muted-foreground">
                    Deforestation rate
                  </span>
                  <span className="text-sm font-bold text-red-500">
                    {defoStat}
                  </span>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  <span>
                    {new Date(item.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <Clock className="w-3 h-3 ml-2" />
                  <span>
                    {new Date(item.created_at).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    className="flex-1 bg-[#40e0d0] hover:bg-[#40e0d0]/80 text-black text-xs"
                    onClick={() => onLoad(item)}
                  >
                    <UploadCloud className="w-3 h-3 mr-1" />
                    Load
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => startRename(item)}
                    disabled={isRenaming}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-500 hover:text-red-600 hover:border-red-300"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
